import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Container, Row, Col, Card, Button, Badge, Collapse } from "react-bootstrap";
import {
  FaMobileAlt,
  FaTabletAlt,
  FaWifi,
  FaHome,
  FaShoppingCart,
  FaCamera,
  FaBatteryFull,
  FaMemory,
  FaChevronDown,
  FaChevronUp,
  FaInfoCircle,
} from "react-icons/fa";
import { motion } from "framer-motion";
import deviceCatalogue from "./deviceCatalogue";

const cardFadeVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const DeviceCard = ({ device, t }) => {
  const [showSpecs, setShowSpecs] = useState(false);
  const specsId = `device-specs-${device.id}`;

  // Highlight lines may carry a qualifier ("rear", "ram", ...) that is translated
  // around the raw value, e.g. "13 MP (Rear)" / "13 MP (arrière)".
  const renderLine = (line) =>
    line.qualifier
      ? t(`catalogue.qualifiers.${line.qualifier}`, { value: line.value })
      : line.value;

  const highlightRows = [
    {
      key: "display",
      icon: device.type === "tablet" ? <FaTabletAlt /> : <FaMobileAlt />,
      lines: device.highlights.display,
    },
    { key: "camera", icon: <FaCamera />, lines: device.highlights.camera },
    { key: "battery", icon: <FaBatteryFull />, lines: device.highlights.battery },
    { key: "memory", icon: <FaMemory />, lines: device.highlights.memory },
  ];

  return (
    <Card className="h-100 shadow-sm border-success">
      <div className="bg-white rounded-top p-2">
        <Card.Img
          variant="top"
          src={device.image}
          alt={t("catalogue.imageAlt", { name: device.name })}
          loading="lazy"
          style={{ height: "220px", objectFit: "contain" }}
        />
      </div>
      <Card.Body className="d-flex flex-column text-center p-2">
        <h4 className="fw-bold text-success mb-1">{device.name}</h4>
        <p className="text-muted mb-2">{t(`catalogue.categories.${device.categoryKey}`)}</p>
        {device.badgeKey && (
          <div className="mb-2">
            <Badge bg="warning" text="dark">
              {t(`catalogue.badges.${device.badgeKey}`)}
            </Badge>
          </div>
        )}

        <ul className="list-unstyled text-start mt-2 mb-3">
          {highlightRows.map((row) => (
            <li key={row.key} className="d-flex align-items-start mb-2">
              <span
                className="text-success me-3 mt-1"
                title={t(`catalogue.highlightLabels.${row.key}`)}
                aria-label={t(`catalogue.highlightLabels.${row.key}`)}
              >
                {row.icon}
              </span>
              <span className="small">
                {row.lines.map((line, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <br />}
                    {renderLine(line)}
                  </React.Fragment>
                ))}
              </span>
            </li>
          ))}
        </ul>

        <div className="mt-auto w-100">
          <Button
            variant="outline-success"
            size="sm"
            className="w-100 mb-2"
            onClick={() => setShowSpecs((open) => !open)}
            aria-controls={specsId}
            aria-expanded={showSpecs}
          >
            {showSpecs ? t("catalogue.hideSpecs") : t("catalogue.fullSpecs")}{" "}
            {showSpecs ? <FaChevronUp /> : <FaChevronDown />}
          </Button>
          <Collapse in={showSpecs}>
            <div id={specsId}>
              <dl className="small text-start bg-light border rounded p-2 mb-3">
                {device.specs.map((spec) => (
                  <React.Fragment key={spec.key}>
                    <dt className="text-success">
                      {t(`catalogue.specLabels.${spec.key}`)}
                    </dt>
                    <dd className="mb-2" style={{ overflowWrap: "anywhere" }}>
                      {spec.value.map((line, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <br />}
                          {line}
                        </React.Fragment>
                      ))}
                    </dd>
                  </React.Fragment>
                ))}
              </dl>
            </div>
          </Collapse>
          <Button
            as={Link}
            to={`/contact?device=${encodeURIComponent(device.name)}`}
            onClick={() => window.scrollTo({ top: 0 })}
            variant="success"
            className="w-100"
          >
            <FaInfoCircle className="me-2" />
            {t("catalogue.getInfo")}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

const NewDevices = () => {
  const { t } = useTranslation("newdevices");

  const fadeVariants = {
    hidden: { opacity: 0, y: -50 },
    visible: { opacity: 1, y: 0, transition: { duration: 1 } },
  };

  const deviceTypes = [
    { icon: <FaMobileAlt size={40} />, text: t("deviceTypes.phones") },
    { icon: <FaTabletAlt size={40} />, text: t("deviceTypes.tablets") },
    { icon: <FaWifi size={40} />, text: t("deviceTypes.broadband") },
    { icon: <FaHome size={40} />, text: t("deviceTypes.iot") },
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="slider text-white bg-dark py-5 mt-5">
        <Container>
          <Row className="justify-content-center text-center mt-5">
            <Col lg={9} md={12}>
              <motion.div initial="hidden" animate="visible" variants={fadeVariants}>
                <h1 className="animated fadeInUp mb-3 mt-5 text-white">
                  {t("hero.title")}
                </h1>
                <p className="lead text-white mb-4">
                  {t("hero.subtitle")}
                </p>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Main Content */}
      <Container className="my-5">
        {/* Intro Section */}
        <Row className="justify-content-center mb-5">
          <Col lg={10}>
            <p className="lead text-center">{t("intro.paragraph")}</p>
          </Col>
        </Row>

        {/* Device Types */}
        <section className="bg-light py-5 px-4 rounded mb-5">
          <h3 className="text-center fw-bold mb-4">{t("deviceTypes.title")}</h3>
          <Row className="g-4">
            {deviceTypes.map((device, index) => (
              <Col key={index} md={6} lg={3}>
                <div className="text-center">
                  <div className="text-success mb-3">{device.icon}</div>
                  <p className="fw-bold">{device.text}</p>
                </div>
              </Col>
            ))}
          </Row>
        </section>

        {/* Device Catalogue */}
        <section className="mb-5" id="device-catalogue">
          <div className="text-center mb-4">
            <h3 className="fw-bold">{t("catalogue.title")}</h3>
            <p className="text-muted mb-0">{t("catalogue.subtitle")}</p>
          </div>
          <Row className="g-4">
            {deviceCatalogue.map((device) => (
              <Col key={device.id} xs={12} md={6} lg={3}>
                <motion.div
                  className="h-100"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.2 }}
                  variants={cardFadeVariants}
                >
                  <DeviceCard device={device} t={t} />
                </motion.div>
              </Col>
            ))}
          </Row>
        </section>

        {/* Prepaid Bundles Section */}
        <Row className="mb-5">
          <Col md={12}>
            <div className="text-center mb-4">
              <FaShoppingCart size={60} className="text-success mb-3" />
              <h3 className="fw-bold">{t("bundles.title")}</h3>
              <p>{t("bundles.description")}</p>
            </div>
          </Col>
        </Row>

        <Row className="g-4 mb-5">
          <Col md={6}>
            <Card className="h-100 shadow-sm border-success">
              <Card.Body className="text-center p-4">
                <h4 className="fw-bold text-success mb-3">{t("bundles.instantConnect.title")}</h4>
                <p>{t("bundles.instantConnect.description")}</p>
                <Button variant="success" className="mt-3">
                  {t("bundles.learnMore")}
                </Button>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="h-100 shadow-sm border-success">
              <Card.Body className="text-center p-4">
                <h4 className="fw-bold text-success mb-3">{t("bundles.easyConnect.title")}</h4>
                <p>{t("bundles.easyConnect.description")}</p>
                <Button variant="success" className="mt-3">
                  {t("bundles.learnMore")}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* CTA Section */}
        <Row className="justify-content-center">
          <Col lg={8} className="text-center">
            <div className="bg-light p-5 rounded">
              <h4 className="fw-bold mb-3">{t("cta.title")}</h4>
              <p className="mb-4">{t("cta.description")}</p>
              <Button variant="success" size="lg" href="/contact">
                {t("cta.buttonText")}
              </Button>
            </div>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default NewDevices;
